import { PropsWithChildren, useRef, useState } from 'react';

import {
  arrow,
  autoPlacement,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  useFloating,
  useTransitionStyles,
  type Placement,
  type Strategy,
} from '@floating-ui/react';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from 'utils/helpers';

const tooltipTitle = cva(['text-left', 'w-full font-semibold'], {
  variants: {
    theme: {
      light: ['text-secondary-dark'],
      dark: ['text-grey-0'],
    },
  },
});

const tooltipContainer = cva(
  [
    'z-50',
    'rounded-[6px]',
    'px-3 py-3',
    'max-w-xs',
    'leading-[20px] text-xs tracking-[0.04em]',
    'whitespace-normal',
  ],
  {
    variants: {
      theme: {
        light: ['bg-fill-tertiary border border-secondary-lightest'],
        dark: [' bg-secondary-dark text-fill-tertiary'],
      },
    },
  },
);

const tooltipDescription = cva(['w-full text-left'], {
  variants: {
    theme: {
      light: ['text-content'],
      dark: ['text-grey-0'],
    },
  },
});

export type TooltipProps = VariantProps<typeof tooltipContainer> & {
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  showTooltip?: boolean;
  onMouseLeave?: () => void;
  placement?: Placement;
  className?: string;
  tooltipContainerClassName?: string;
  hideArrow?: boolean;
  strategy?: Strategy;
  visibilityBehavior?: 'flip' | 'autoplacement';
  mainAxisOffset?: number;
};

const Tooltip: React.FC<PropsWithChildren<TooltipProps>> = ({
  theme = 'light',
  placement = 'top',
  children,
  title,
  description,
  showTooltip = false,
  className,
  tooltipContainerClassName,
  onMouseLeave,
  hideArrow,
  strategy = 'absolute',
  visibilityBehavior = 'flip',
  mainAxisOffset = 10,
}) => {
  const arrowRef = useRef(null);

  const [open, setIsOpen] = useState(showTooltip);
  const { refs, floatingStyles, context } = useFloating({
    open,
    strategy,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      visibilityBehavior === 'flip' ? flip() : autoPlacement(),
      offset({ mainAxis: mainAxisOffset }),
      arrow({ element: arrowRef }),
    ],
  });

  const { isMounted, styles } = useTransitionStyles(context);

  const props = !showTooltip
    ? {
        onMouseEnter: () => setIsOpen(true),
        onMouseLeave: () => setIsOpen(false),
      }
    : {};
  return (
    <div className={cn('group')} onMouseLeave={onMouseLeave}>
      <div
        {...props}
        ref={refs.setReference}
        className={cn('w-fit flex items-center justify-center', className)}
      >
        {children}
      </div>
      {isMounted && (
        <div
          {...props}
          className={cn(
            tooltipContainer({ theme }),
            theme,
            tooltipContainerClassName,
          )}
          ref={refs.setFloating as any}
          style={{
            ...styles,
            ...floatingStyles,
          }}
        >
          <div className={cn(tooltipTitle({ theme }))}>{title}</div>
          <div
            className={cn(
              tooltipDescription({ theme }),
              description ? 'block' : 'hidden',
            )}
          >
            {description}
          </div>
          {!hideArrow && (
            <FloatingArrow
              className={cn(
                theme === 'dark'
                  ? 'fill-secondary-dark'
                  : 'fill-fill-tertiary [&>path:last-of-type]:stroke-secondary-lightest',
              )}
              ref={arrowRef}
              context={context}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
